import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { Box, Grid, Stack, Typography } from "@mui/material";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";

import { Section, type SectionVariant } from "./Section";

const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1400&q=80";

type ValueKey = "creativity" | "elegance" | "trust";

const VALUE_ICONS: Record<ValueKey, ComponentType> = {
  creativity: AutoAwesomeOutlinedIcon,
  elegance: DiamondOutlinedIcon,
  trust: VerifiedOutlinedIcon,
};

const VALUE_KEYS: ValueKey[] = ["creativity", "elegance", "trust"];

interface AboutSectionProps {
  variant?: SectionVariant;
}

export function AboutSection({ variant }: AboutSectionProps = {}) {
  const { t } = useTranslation(["landing", "common"]);

  return (
    <Section
      id="about"
      variant={variant}
      eyebrow={t("common:eyebrow.studio")}
      title={t("landing:about.title")}
    >
      <Grid container spacing={{ xs: 4, md: 8 }} alignItems="stretch">
        <Grid size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              width: "100%",
              height: { xs: 280, md: "100%" },
              minHeight: { md: 360 },
              borderRadius: 3,
              backgroundImage: `url(${ABOUT_IMAGE})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              boxShadow: "0 20px 60px -30px rgba(0,0,0,0.35)",
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              {t("landing:about.lead")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("landing:about.body")}
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {VALUE_KEYS.map((key) => {
                const Icon = VALUE_ICONS[key];
                return (
                  <Grid key={key} size={{ xs: 12, sm: 4 }}>
                    <Stack spacing={1.2}>
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={(theme) => ({
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.mode === "dark" ? "rgba(201,163,90,0.18)" : "rgba(201,163,90,0.14)",
                          color: theme.palette.primary.main,
                        })}
                      >
                        <Icon />
                      </Stack>
                      <Typography variant="h6">{t(`landing:about.values.${key}.title`)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(`landing:about.values.${key}.description`)}
                      </Typography>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );
}
